import "@/index.css";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Menu, X, Instagram, Youtube, Linkedin, ArrowLeft, ChevronDown, Play } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NYZA Logo URL
const NYZA_LOGO_URL = "https://customer-assets.emergentagent.com/job_nyza-redesign/artifacts/hi158gb6_nyza%20logo.png";

// Animated Background Orbs
const AnimatedOrbs = () => (
  <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
    <div className="glow-orb w-96 h-96 bg-purple-600/30" style={{ top: '10%', right: '10%', animationDelay: '0s' }} />
    <div className="glow-orb w-80 h-80 bg-blue-600/30" style={{ bottom: '20%', left: '5%', animationDelay: '2s' }} />
    <div className="glow-orb w-64 h-64 bg-violet-500/20" style={{ top: '50%', left: '50%', animationDelay: '4s' }} />
  </div>
);

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '/' },
    { label: 'References', href: '/references', isLink: true },
    { label: 'Services', href: '/#services' },
    { label: 'Contact', href: '/#contact' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'py-3' : 'py-6'}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="relative z-10">
            <img src={NYZA_LOGO_URL} alt="NYZA Studio" className={`transition-all duration-300 ${scrolled ? 'h-8' : 'h-10'}`} />
          </Link>
          <div className="hidden md:flex items-center relative z-10">
            <div className="bg-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-full px-2 py-2 flex items-center gap-1 shadow-lg">
              {navItems.map((item) => (
                item.isLink ? (
                  <Link key={item.label} to={item.href} className="text-slate-300 hover:text-white hover:bg-slate-700/50 px-4 py-2 rounded-full transition-all text-sm font-medium">{item.label}</Link>
                ) : (
                  <a key={item.label} href={item.href} className="text-slate-300 hover:text-white hover:bg-slate-700/50 px-4 py-2 rounded-full transition-all text-sm font-medium">{item.label}</a>
                )
              ))}
            </div>
          </div>
          <button className="md:hidden p-2 text-white relative z-10" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {isOpen && (
          <div className="md:hidden mt-4 relative z-10">
            <div className="bg-purple-950/40 backdrop-blur-2xl border border-purple-500/30 rounded-2xl p-6 flex flex-col gap-2 shadow-glow">
              {navItems.map((item) => (
                item.isLink ? (
                  <Link key={item.label} to={item.href} className="block text-slate-200 hover:text-white py-3 px-4 text-lg font-medium" onClick={() => setIsOpen(false)}>{item.label}</Link>
                ) : (
                  <a key={item.label} href={item.href} className="block text-slate-200 hover:text-white py-3 px-4 text-lg font-medium" onClick={() => setIsOpen(false)}>{item.label}</a>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-slate-800 relative z-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6">
          <div className="flex justify-center md:justify-start">
            <Link to="/"><img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-8" /></Link>
          </div>
          <div className="flex items-center justify-center gap-4">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-purple-400 transition-colors"><Instagram className="w-5 h-5" /></a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-purple-400 transition-colors"><Youtube className="w-5 h-5" /></a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-purple-400 transition-colors"><Linkedin className="w-5 h-5" /></a>
          </div>
          <div className="flex items-center justify-center md:justify-end gap-4 text-sm text-slate-500">
            <Link to="/impressum" className="hover:text-purple-400 transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-purple-400 transition-colors">Datenschutz</Link>
            <Link to="/agb" className="hover:text-purple-400 transition-colors">AGBs</Link>
          </div>
        </div>
        <div className="text-center mt-6">
          <p className="text-sm text-slate-600">© {new Date().getFullYear()} NYZA Studio</p>
        </div>
      </div>
    </footer>
  );
};

// Extract YouTube Video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Image-Text Block Component - Alternating Layout
const ImageTextBlock = ({ block, index }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-12 items-center py-12 md:py-20`}>
      {/* Image */}
      <div className="w-full md:w-1/2">
        <div className="relative rounded-2xl overflow-hidden aspect-video ring-1 ring-slate-700/50 group">
          <img 
            src={block.image} 
            alt={block.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      </div>
      
      {/* Text Content */}
      <div className="w-full md:w-1/2">
        {block.title && (
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{block.title}</h3>
        )}
        <div className="text-slate-400 leading-relaxed whitespace-pre-wrap">{block.text}</div>
      </div>
    </div>
  );
};

// Video Block Component
const VideoBlock = ({ block }) => {
  const videoId = getYouTubeVideoId(block.video_url);
  
  if (!videoId) return null;
  
  return (
    <div className="py-12 md:py-20">
      {block.title && (
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">{block.title}</h3>
      )}
      {block.text && (
        <p className="text-slate-400 text-center mb-8 max-w-2xl mx-auto">{block.text}</p>
      )}
      <div className="relative rounded-2xl overflow-hidden aspect-video max-w-4xl mx-auto ring-1 ring-slate-700/50">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={block.title || 'Video'}
        />
      </div>
    </div>
  );
};

// Gallery Block Component
const GalleryBlock = ({ block }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const images = block.gallery_images || [];
  
  if (images.length === 0) return null;
  
  return (
    <div className="py-12 md:py-20">
      {block.title && (
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">{block.title}</h3>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img, idx) => (
          <div
            key={idx}
            className="aspect-square rounded-xl overflow-hidden ring-1 ring-slate-700/50 cursor-pointer group"
            onClick={() => setSelectedImage(img)}
          >
            <img 
              src={img} 
              alt={`Gallery ${idx + 1}`} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
            />
          </div>
        ))}
      </div>
      
      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-6 right-6 text-white hover:text-purple-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage} 
              alt="Gallery Preview" 
              className="max-w-full max-h-[90vh] object-contain rounded-xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// FAQ Block Component
const FAQBlock = ({ block }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqItems = block.faq_items || [];
  
  if (faqItems.length === 0) return null;
  
  return (
    <div className="py-12 md:py-20 max-w-3xl mx-auto">
      {block.title && (
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">{block.title}</h3>
      )}
      <div className="flex flex-col gap-6">
        {faqItems.map((faq, idx) => (
          <div
            key={idx}
            className="card-glow overflow-hidden rounded-xl"
          >
            <button 
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              className="w-full px-6 py-5 flex items-center justify-between text-left"
            >
              <span className="text-white font-medium pr-4">{faq.question}</span>
              <ChevronDown className={`w-5 h-5 text-purple-400 flex-shrink-0 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {openIndex === idx && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 text-slate-400 leading-relaxed border-t border-slate-700/50 pt-4">
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// CTA Block Component - Full Width Edge-to-Edge
const CTABlock = ({ block }) => {
  const isExternal = block.cta_button_link?.startsWith('http');
  
  return (
    <div className="py-8 md:py-12 relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
      <div className="relative overflow-hidden bg-gradient-to-r from-purple-600/50 via-violet-600/50 to-blue-600/50 border-y border-purple-500/40">
        {/* Glow effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-64 h-32 bg-purple-500/30 rounded-full blur-3xl" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-64 h-32 bg-blue-500/30 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-6 md:py-8 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-12">
          {/* Text - Left */}
          <div className="text-center md:text-left">
            {block.title && (
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">{block.title}</h3>
            )}
            {block.text && (
              <p className="text-slate-200 mt-1 text-sm md:text-base">{block.text}</p>
            )}
          </div>
          
          {/* Button - Right */}
          {block.cta_button_text && block.cta_button_link && (
            isExternal ? (
              <a 
                href={block.cta_button_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glow px-6 md:px-8 py-3 md:py-4 rounded-xl text-white font-semibold whitespace-nowrap flex-shrink-0"
              >
                {block.cta_button_text}
              </a>
            ) : (
              <Link 
                to={block.cta_button_link}
                className="btn-glow px-6 md:px-8 py-3 md:py-4 rounded-xl text-white font-semibold whitespace-nowrap flex-shrink-0"
              >
                {block.cta_button_text}
              </Link>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Single Image Block Component
const SingleImageBlock = ({ block }) => {
  return (
    <div className="py-12 md:py-20">
      {block.title && (
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">{block.title}</h3>
      )}
      {block.text && (
        <p className="text-slate-400 text-center mb-8 max-w-2xl mx-auto">{block.text}</p>
      )}
      <div className="relative rounded-2xl overflow-hidden ring-1 ring-slate-700/50 group max-w-5xl mx-auto">
        <img 
          src={block.image} 
          alt={block.title || 'Image'} 
          className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
    </div>
  );
};

// Universal Content Block Renderer
const ContentBlock = ({ block, index }) => {
  const blockType = block.type || 'image_text';
  
  switch (blockType) {
    case 'video':
      return <VideoBlock block={block} />;
    case 'gallery':
      return <GalleryBlock block={block} />;
    case 'faq':
      return <FAQBlock block={block} />;
    case 'cta':
      return <CTABlock block={block} />;
    case 'single_image':
      return <SingleImageBlock block={block} />;
    case 'image_text':
    default:
      return <ImageTextBlock block={block} index={index} />;
  }
};

// Custom Page Component
const CustomPageView = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/pages/${slug}`);
        setPage(response.data);
      } catch (err) {
        setError('Page not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  // Safari/Atlas fix: scroll to top and trigger IntersectionObserver
  useEffect(() => {
    window.scrollTo(0, 0);
    // Micro-scroll to trigger IntersectionObserver for Safari
    const timer = setTimeout(() => {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }, 150);
    return () => clearTimeout(timer);
  }, [slug]);

  // Update document title and meta tags for SEO
  useEffect(() => {
    if (!page) return;
    
    // Set document title
    const seoTitle = page.seo_title || `${page.title} | NYZA Studio`;
    document.title = seoTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page.seo_description) {
      metaDescription.setAttribute('content', page.seo_description);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    
    if (ogTitle) ogTitle.setAttribute('content', seoTitle);
    if (ogDescription && page.seo_description) ogDescription.setAttribute('content', page.seo_description);
    if (ogImage && page.seo_image) ogImage.setAttribute('content', page.seo_image);
    
    // Safari fix: trigger IntersectionObserver after page data loads
    const timer = setTimeout(() => {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }, 200);
    
    // Cleanup on unmount - restore original values
    return () => {
      clearTimeout(timer);
      document.title = 'NYZA Studio | WHERE PIXELS MEET MAGIC';
    };
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-slate-400 mb-8">Page not found</p>
          <Link to="/" className="btn-glow px-6 py-3 rounded-xl text-white font-medium inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-x-hidden">
      <AnimatedOrbs />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-12 relative z-10">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/#portfolio" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Portfolio
          </Link>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="text-4xl md:text-5xl lg:text-6xl font-bold"
          >
            <span className="gradient-text">{page.title}</span>
          </motion.h1>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="relative z-10">
        <div className="max-w-6xl mx-auto px-4">
          {page.blocks && page.blocks.length > 0 ? (
            page.blocks.sort((a, b) => a.order - b.order).map((block, index) => (
              <ContentBlock key={block.id} block={block} index={index} />
            ))
          ) : (
            <div className="text-center py-20 text-slate-500">No content yet.</div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative z-10">
        <div className="max-4xl mx-auto px-4 text-center">
          <div className="card-glow p-8 md:p-12 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Interested in a similar project?</h2>
            <p className="text-slate-400 mb-8">Let's discuss your ideas and create something amazing together.</p>
            <Link to="/#contact" className="btn-glow px-8 py-4 rounded-xl text-white font-medium inline-block">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomPageView;
